import { ToggleButtonGroup, ToggleButton, Paper, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import Chart from "react-google-charts";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";

interface StatsDashboardProps {
    authToken: string,
}

interface RecordsSummary {
    results: AnnualSummary[]
}

interface DataMode {
    mode: string,
    graphTitle: string,
    apiUrl: string
}

interface AnnualSummary {
    year: number,
    numberOfRetractions: number,
    numberOfExpressionsOfConcern: number,
    numberOfCorrections: number
}

function RetractionPerYearLinearCharts() {
    const [hasInitialData, setHasInitialData] = useState(false)
    const [recordsSummary, setRecordsSummary] = useState<any[][]>()
    const recordDateMode: DataMode = {
        mode: 'record-date',
        graphTitle: 'Retractions by Year (Retraction Date)',
        apiUrl: argosApiEntryPoint + '/stats/records-per-record-year'
    }
    const publicationDateMode: DataMode = {
        mode: 'publication-date',
        graphTitle: 'Retractions by Publication Year',
        apiUrl: argosApiEntryPoint + '/stats/records-per-publication-year'
    }
    const [mode, setMode] = useState(recordDateMode)
    const columnHeaders: any[][] = [
        [
            { type: "date", label: "Year" },
            { type: "number", label: "Retractions" },
            { type: "number", label: "Expressions of Concern" },
            { type: "number", label: "Corrections" },
        ]
    ]

    useEffect(() => {

        async function fetchData() {
            let authToken = await auth.currentUser?.getIdToken(true)

            fetch(mode.apiUrl
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: RecordsSummary) => {
                    setHasInitialData(true)
                    let numericalSummaries: any[][] = data.results
                        .map(it => [new Date(it.year, 11, 31), it.numberOfRetractions, it.numberOfExpressionsOfConcern, it.numberOfCorrections])
                    setRecordsSummary(columnHeaders.concat(numericalSummaries))
                })
        }
        fetchData()
    }, [mode]);

    const options = {
        legend: { position: "bottom", textStyle: { color: '#003366', fontName: 'Roboto', fontSize: 14 } },
        selectionMode: 'multiple',
        tooltip: { trigger: 'selection' },
        aggregationTarget: 'category',
        series: {
            0: { color: '#d32f2f' },
            1: { color: '#ed6c02' },
            2: { color: '#1565c0' },
        },
        hAxis: {
            textStyle: { fontName: 'Roboto', fontSize: 14 }
        },
        vAxis: { textStyle: { fontName: 'Roboto', fontSize: 14 } }


    };

    const handleChange = (
        event: React.MouseEvent<HTMLElement>,
        newMode: string,
    ) => {
        if (newMode === "record-date") {
            setMode(recordDateMode)
        } else {
            setMode(publicationDateMode)
        }
    };

    return <Paper elevation={5} sx={{ padding: 2, width: '90%' }}>
        <Grid container>
            <Grid item xs={12} padding={2}>
                <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h5">
                    {mode.graphTitle}
                </Typography>
            </Grid>
            <Grid item xs={12} padding={2}>
                <ToggleButtonGroup
                    color="primary"
                    value={mode.mode}
                    exclusive
                    onChange={handleChange}
                    aria-label="Graph Mode"
                >
                    <ToggleButton value="record-date">By Retraction Date</ToggleButton>
                    <ToggleButton value="publication-date">By Publication Date</ToggleButton>
                </ToggleButtonGroup></Grid>{hasInitialData &&
                    <Grid item xs={12} sx={{ minHeight: '400px' }}>
                        <Chart
                            chartType="LineChart"
                            width="100%"
                            height="80%"
                            data={recordsSummary}
                            options={options}
                            chartPackages={["corechart", "controls"]}
                            controls={[
                                {
                                    controlType: "ChartRangeFilter",
                                    options: {
                                        filterColumnIndex: 0,
                                        ui: {
                                            chartType: "LineChart",
                                            chartOptions: {
                                                chartArea: { width: "80%", height: "50%" },
                                                hAxis: { baselineColor: "none" },
                                                series: {
                                                    0: { color: '#d32f2f' },
                                                    1: { color: '#ed6c02' },
                                                    2: { color: '#1565c0' },
                                                }
                                            },
                                        },

                                    },
                                    controlPosition: "bottom",
                                    controlWrapperParams: {
                                        state: {
                                            range: {
                                                start: new Date((new Date()).getFullYear() - 15, 11, 31),
                                                end: new Date((new Date()).getFullYear() - 1, 11, 31),
                                            },
                                        }
                                    },
                                },
                            ]}
                        /></Grid>
            }
            <Grid item xs={12} paddingRight={2} sx={{
                paddingTop: {
                    xs: 2,
                    md: 0
                }
            }} >
                <Typography fontFamily='Roboto' color='#003366' align="right" sx={{ fontStyle: 'italic' }}>
                    Powered by Scitility PBC
                </Typography>
            </Grid>
        </Grid>
    </Paper>
}

export default RetractionPerYearLinearCharts
